import React, { useImperativeHandle, useMemo, useRef } from 'react';
import { Box, Alert } from '@mui/material';
import {
  GroupedSelect,
  type GroupedSelectOption,
  type GroupedSelectHandle,
} from '../dropdown';
import type { TicketTypeTemplate } from '../../types/ticketTypeTemplate';

interface GroupedTicketTypePickerProps {
  value: TicketTypeTemplate | null;
  onChange: (template: TicketTypeTemplate | null) => void;
  templates: TicketTypeTemplate[];
  isLoading?: boolean;
  outOfServiceWarning?: boolean;
  onTabAway?: () => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
}

export interface GroupedTicketTypePickerHandle {
  focus: () => void;
}

export const GroupedTicketTypePicker = React.forwardRef<GroupedTicketTypePickerHandle, GroupedTicketTypePickerProps>(({
  value,
  onChange,
  templates,
  isLoading = false,
  outOfServiceWarning = false,
  onTabAway,
  disabled = false,
  label = 'Ticket Type',
  placeholder = 'Search or select ticket type',
}, ref) => {
  const selectRef = useRef<GroupedSelectHandle>(null);

  useImperativeHandle(ref, () => ({
    focus: () => selectRef.current?.focus(),
  }), []);

  const options = useMemo<GroupedSelectOption[]>(
    () => templates.map((t) => ({
      value: t.ticketTypeId,
      label: t.displayName || t.ticketTypeId,
      description: t.billingMode === 'hours' ? 'Billed by hours' : 'Billed by units',
      group: t.group,
    })),
    [templates],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <GroupedSelect
        ref={selectRef}
        label={label}
        placeholder={placeholder}
        value={value?.ticketTypeId ?? null}
        options={options}
        disabled={disabled}
        onTabAway={onTabAway}
        emptyText={isLoading ? 'Loading…' : 'No ticket types'}
        onChange={(val) => {
          onChange(val ? templates.find((t) => t.ticketTypeId === val) ?? null : null);
        }}
      />

      {outOfServiceWarning && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          You have selected a product for a service that may not exist for this customer — please check before saving this ticket.
        </Alert>
      )}
    </Box>
  );
});
